import React, { useRef } from 'react';
import styled from 'styled-components';
import { Border, Breakpoints, Color, Grid, Type } from '../../app/StyleGuide';
import { BrandButton, WhiteButton } from '../../components/Buttons';
import { useLogin } from './hooks/useLogin';
import windowService from '../../services/windowService';
import LoadingState from '../../components/LoadingState';

const LoginMessage = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${Grid._5};
  align-items: center;
  background: #b3df763c;
  border-radius: ${Border.radius};
  box-shadow: inset 0 0 0 1px #a4cc6c3c;
  margin: ${Grid._5} 0;
  padding: ${Grid._4};
  color: ${Color.Green._50};

  @media screen and (min-width: ${Breakpoints.screen_sm}) {
    padding: ${Grid._4} ${Grid._6};
  }

  & div {
    display: flex;
    flex-direction: column;
    text-align: left;
  }
`;

const LoginMessageTitle = styled.span`
  font-weight: ${Type.Weight.bold};
`;

const LoginMessageMessage = styled.span`
  font-size: ${Type.Scale._1};
`;

const LoginContainer = styled.div`
  display: flex;
  min-height: 100vh;
  align-items: center;
  justify-content: center;
  background-color: ${Color.Gray._10};
`;

const Logo = styled.img`
  width: 225px;
  margin-bottom: ${Grid._6};
`;

const LoginFormContainer = styled.div<{ offsetX: string }>`
  max-width: 375px;
  width: 100%;
  text-align: center;
  margin-top: ${props => props.offsetX};

  h1 {
    margin-bottom: ${Grid._2};
    color: ${Type.Color.dark};
  }

  p {
    color: ${Type.Color.medium};
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Grid._4};
`;

const Divider = styled.div`
  display: flex;
  align-items: center;
  text-align: center;
  margin: ${Grid._4} 0;

  &::before,
  &::after {
    content: '';
    flex: 1;
    border-bottom: 1px solid ${Color.Gray._30};
  }

  span {
    padding: 0 ${Grid._4};
    color: ${Type.Color.medium};
  }
`;

const LoginButton = styled(BrandButton).attrs({
  // Only needed for legacy Selenium tests
  // TODO: Remove once Selenium tests are migrated
  'data-qa-hook': 'logInContinue',
})``;
const RegisterButton = styled(WhiteButton)``;

const CopyrightText = styled.p`
  color: ${Color.Gray._70};
  font-size: ${Type.Scale._1};
  margin: 0;
  padding: 0;
  text-align: center;
`;

const LegalLinks = styled.div`
  display: flex;
  justify-content: center;
  gap: ${Grid._4};
  margin-top: ${Grid._2};

  a {
    color: ${Color.Gray._70};
    font-size: ${Type.Scale._1};
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
`;

export function Login() {
  const { checkingSession, loginMessage, getOriginBranding, redirectToLogin, redirectToRegister } = useLogin();
  const loginMessageRef = useRef<HTMLDivElement>(null);

  if (checkingSession) {
    return <LoadingState />;
  }

  return (
    <LoginContainer>
      <LoginFormContainer
        offsetX={loginMessage?.message?.title !== undefined ? `-${loginMessageRef.current?.clientHeight}px` : '0'}
      >
        {loginMessage?.isLoading ? (
          <LoadingState />
        ) : (
          loginMessage?.message && (
            <LoginMessage ref={loginMessageRef}>
              <i className="fas fa-check"></i>
              <div>
                <LoginMessageTitle>{loginMessage.message.title}</LoginMessageTitle>
                <LoginMessageMessage>{loginMessage.message.subtitle}</LoginMessageMessage>
              </div>
            </LoginMessage>
          )
        )}

        {loginMessage?.invalidEasyLink && !loginMessage.isLoading && (
          <LoginMessage ref={loginMessageRef}>
            <i className="fas fa-exclamation-triangle"></i>
            <div>
              <LoginMessageTitle>This link has been deactivated.</LoginMessageTitle>
              <LoginMessageMessage>
                Oops! It looks like you&apos;ve been given a bad link. Reach out to your administrator and validate the
                url.
              </LoginMessageMessage>
            </div>
          </LoginMessage>
        )}

        <Logo src={getOriginBranding().logoSrc} alt={getOriginBranding().alt} />

        <ButtonContainer>
          <LoginButton className="steps-primary-button" onClick={() => redirectToLogin()}>
            Log In
          </LoginButton>
          <Divider>
            <span>Don&apos;t have an account?</span>
          </Divider>
          <RegisterButton onClick={() => redirectToRegister()}>Register</RegisterButton>
          <div>
            <CopyrightText>Copyright© {new Date().getFullYear()}. All rights reserved.</CopyrightText>
            <LegalLinks>
              <a onClick={() => windowService.openUrl('https://www.lifeway.com/en/about/privacy-policy')}>Privacy</a>
              <a onClick={() => windowService.openUrl('https://www.lifeway.com/en/about/terms-of-use')}>Terms</a>
            </LegalLinks>
          </div>
        </ButtonContainer>
      </LoginFormContainer>
    </LoginContainer>
  );
}
